/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Monotonically increasing ID used to cancel stale correction loops.
 * Every call to scrollToFileElement (or cancelScrollCorrection) bumps this,
 * causing any in-flight correction loop to bail out on its next frame.
 */
let activeScrollId = 0;

/**
 * Cancel any running scroll-correction loop. Call this before performing
 * a different scroll (e.g., jumping to a comment) so the correction loop
 * doesn't fight it.
 */
export function cancelScrollCorrection(): void {
  activeScrollId++;
}

/**
 * Scrolls to the element matching `[data-toc-path="<path>"]` and keeps
 * correcting the scroll position as layout shifts occur (e.g., lazy-loaded
 * diffs expanding above the target).
 *
 * Uses a brief polling loop with requestAnimationFrame: each frame it checks
 * whether the element has drifted from the top of the viewport and re-scrolls
 * if necessary. Stops after the position is stable for several consecutive
 * frames, or after a timeout.
 *
 * Any previous correction loop is automatically cancelled when a new one
 * starts.
 */
export default function scrollToFileElement(path: string): void {
  const element = document.querySelector(
    `[data-toc-path="${CSS.escape(path)}"]`,
  );
  if (element == null) {
    return;
  }

  // Cancel any previously running correction loop.
  const myScrollId = ++activeScrollId;

  // Initial scroll.
  element.scrollIntoView({behavior: 'auto', block: 'start'});

  const MAX_DURATION_MS = 3000;
  const STABLE_FRAMES_REQUIRED = 10;
  const startTime = performance.now();
  let stableFrames = 0;
  let lastTop: number | null = null;

  function correct(): void {
    // Bail out if a newer scroll has taken over.
    if (activeScrollId !== myScrollId) {
      return;
    }

    if (performance.now() - startTime > MAX_DURATION_MS) {
      return;
    }

    const rect = element!.getBoundingClientRect();
    // "At the top of the viewport" means rect.top ≈ 0. Allow a small
    // tolerance for sub-pixel differences and sticky headers.
    const TOLERANCE_PX = 2;

    if (lastTop != null && Math.abs(rect.top - lastTop) < TOLERANCE_PX) {
      stableFrames++;
      if (stableFrames >= STABLE_FRAMES_REQUIRED) {
        return; // Position has stabilized — stop correcting.
      }
    } else {
      stableFrames = 0;
    }
    lastTop = rect.top;

    if (Math.abs(rect.top) > TOLERANCE_PX) {
      // Element has shifted — re-scroll. Use instant scroll to avoid
      // fighting with smooth scroll animations.
      window.scrollBy({top: rect.top, behavior: 'auto'});
    }

    requestAnimationFrame(correct);
  }

  // Start the correction loop on the next frame (after the browser has
  // a chance to lay out any newly activated diff content).
  requestAnimationFrame(correct);
}
